import { motion } from "framer-motion";
import {
  Clock, UserCheck, ShieldCheck, Signal, Zap,
  PackageCheck, Store, Truck, Boxes, XCircle, UserX, Ban, RotateCcw, CheckCircle2,
  Percent, Gauge, Hourglass, AlarmClock, Clock4, AlertTriangle, TrendingUp, TrendingDown, Minus,
} from "lucide-react";

// Pick an icon for each Keeta metric by its Arabic label so cards feel organized & scannable.
const iconFor = (label) => {
  const l = label;
  if (l.includes("هل أنت في الوردية")) return UserCheck;
  if (l.includes("شريك التوصيل")) return ShieldCheck;
  if (l.includes("attendance_summary")) return CheckCircle2;
  if (l.includes("وقت الذروة") || l.includes("الذروة")) return Zap;
  if (l.includes("courier_valid_online") || l.includes("اتصال السائقين") || l.includes("وقت اتصال")) return Signal;
  if (l.includes("المقبولة")) return PackageCheck;
  if (l.includes("المطاعم")) return Store;
  if (l.includes("تم تسليمها")) return Truck;
  if (l.includes("الكبيرة")) return Boxes;
  if (l.includes("المرفوضة تلقائيًا") || l.includes("تلقائي")) return RotateCcw;
  if (l.includes("المرفوضة (السائق)")) return UserX;
  if (l.includes("المرفوضة")) return XCircle;
  if (l.includes("الإلغاء")) return Ban;
  if (l.includes("اكتمال")) return CheckCircle2;
  if (l.includes("الوقت المحدد") || l.includes("المُحدَّد")) return Gauge;
  if (l.includes("متوسط مدة")) return Hourglass;
  if (l.includes("55 دقيقة")) return AlarmClock;
  if (l.includes("المتأخرة جدًا")) return AlertTriangle;
  if (l.includes("المتأخرة")) return Clock4;
  if (l.includes("وقت") || l.includes("ساعات")) return Clock;
  return Percent;
};

const SECTION_ACCENT = {
  shift: { bar: "#F5C400", chip: "#FFF9E0", text: "#8A6D00", icon: "#B8860B" },
  tasks: { bar: "#0EA5E9", chip: "#E8F6FE", text: "#075985", icon: "#0284C7" },
  exp: { bar: "#8B5CF6", chip: "#F1EBFF", text: "#5B21B6", icon: "#7C3AED" },
};

// Some metrics are "bad when they go up" (rejections, late orders) — invert the colour so
// green always means "good performance" and red means "worse", at a glance.
const isWorseWhenUp = (label) => {
  const l = label;
  return /رفض|مرفوض|إلغاء|الغاء|متأخر|المتأخرة|أكثر من 55|55 دقيقة/.test(l);
};

// Positive change is not always "good"; colour reflects performance direction, not just sign.
const ChangeChip = ({ label, value, worseUp = false }) => {
  if (value === null || value === undefined) return null;
  const pct = Math.abs(value) <= 5 ? value * 100 : value;
  const up = value > 0.0001;
  const down = value < -0.0001;
  const good = up ? !worseUp : down ? worseUp : null; // true=good(green), false=bad(red), null=neutral
  const Ico = up ? TrendingUp : down ? TrendingDown : Minus;
  const color = good === true ? "#059669" : good === false ? "#DC2626" : "#94A3B8";
  return (
    <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold" style={{ color }} title={label}>
      <Ico className="h-3 w-3" />
      {label[0]}: {up ? "+" : ""}{pct.toFixed(1)}%
    </span>
  );
};

const MetricCard = ({ metric, accent, onClick }) => {
  const Icon = iconFor(metric.label);
  const worseUp = isWorseWhenUp(metric.label);
  const clickable = typeof onClick === "function";
  return (
    <motion.div
      whileHover={{ y: -2 }}
      data-testid={`keeta-metric-${metric.key}`}
      onClick={onClick}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={clickable ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick(); } } : undefined}
      className={`rounded-xl border border-slate-200 bg-white p-3 flex flex-col gap-1.5 min-h-[104px] ${clickable ? "cursor-pointer hover:border-blue-300 hover:shadow-md transition" : ""}`}
    >
      <div className="flex items-start justify-between gap-2">
        <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg" style={{ background: accent.chip }}>
          <Icon className="h-4 w-4" style={{ color: accent.icon }} />
        </span>
        <span className="text-[11px] leading-tight text-slate-500 text-start flex-1">{metric.label}</span>
      </div>
      <div className="font-mono-nums text-lg font-extrabold text-slate-800 mt-auto">{metric.display}</div>
      {(metric.daily !== null || metric.weekly !== null) && (
        <div className="flex items-center gap-2 flex-wrap">
          <ChangeChip label="يومي" value={metric.daily} worseUp={worseUp} />
          <ChangeChip label="أسبوعي" value={metric.weekly} worseUp={worseUp} />
        </div>
      )}
    </motion.div>
  );
};

const DRILLABLE_KEETA = ["count", "duration", "minutes", "rate", "yesno"];

export const KeetaDashboard = ({ summary, onMetricDrill }) => {
  if (!summary) return null;
  return (
    <div className="space-y-4" data-testid="keeta-dashboard">
      <div className="flex items-center gap-2 text-sm text-slate-500">
        <UserCheck className="h-4 w-4" />
        <span>عدد السائقين: <b className="text-slate-700">{summary.drivers}</b></span>
      </div>
      {summary.sections.map((sec) => {
        const accent = SECTION_ACCENT[sec.key] || SECTION_ACCENT.tasks;
        if (!sec.metrics?.length) return null;
        return (
          <div key={sec.key} className="rounded-2xl border border-slate-200 bg-slate-50/50 overflow-hidden" data-testid={`keeta-section-${sec.key}`}>
            <div className="flex items-center gap-2 px-4 py-2.5" style={{ background: accent.chip }}>
              <span className="h-4 w-1.5 rounded-full" style={{ background: accent.bar }} />
              <h4 className="font-heading font-bold text-sm" style={{ color: accent.text }}>{sec.title}</h4>
              <span className="text-xs" style={{ color: accent.text }}>({sec.metrics.length})</span>
            </div>
            <div className="p-3 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2.5">
              {sec.metrics.map((m) => (
                <MetricCard key={m.key} metric={m} accent={accent}
                  onClick={onMetricDrill && DRILLABLE_KEETA.includes(m.type) ? () => onMetricDrill(m) : undefined} />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};
